<template>
  <Modal ref="modal" title="Faturamento por Tipo e Status" @cancel="close">
    <div class="loading" v-if="!loaded">
      <LoadingSpinner />
    </div>

    <form v-else class="export-container" @submit.prevent="handleSubmit">
      <div class="input-container">
        <legend>Selecione a(s) Autoescola(s)</legend>

        <div class="inputs-box">
          <div class="checkbox">
            <label>
              <input type="checkbox" v-model="checkAll" />
              <span></span>
              Todas as Autoescolas
            </label>
          </div>

          <div class="checkbox" v-for="item in schemaOptions" :key="item.value">
            <label>
              <input type="checkbox" :value="item.value" v-model="schemas" />
              <span></span>
              {{ item.text }}
            </label>
          </div>
        </div>
      </div>

      <InputMonth legend="Insira a data" :modelValue="mes" @update:modelValue="$event => this.mes = $event"
        :disabled="loading" />

      <div class="format-container">
        <span>Em qual formato de exportação?</span>

        <RadioButtons :options="radioOptions" name="format" :modelValue="format"
          @update:modelValue="$event => this.format = $event" />
      </div>

      <Button type="submit" text="Fazer download" color="primary" :disabled="loading">
        <TrayArrowDown :size="18" />
      </Button>
    </form>
  </Modal>
</template>

<script>
import Modal from 'core/Modal'
import LoadingSpinner from 'core/LoadingSpinner'
import InputMonth from 'core/InputMonth'
import RadioButtons from 'core/RadioButtons'
import Button from 'core/Button'
import TrayArrowDown from 'icons/TrayArrowDown'
import _export from '../../../../services/export'

export default {
  name: 'FaturamentoTipoStatusModal',
  components: {
    Modal,
    LoadingSpinner,
    Button,
    InputMonth,
    RadioButtons,
    TrayArrowDown,
  },
  data() {
    return {
      mes: '',
      format: 'pdf',
      schemaOptions: [],
      schemas: [],
      radioOptions: [
        { value: "pdf", label: "PDF", info: "(Documento)" },
        { value: "csv", label: "CSV", info: "(Planilha)" },
        { value: "txt", label: "TXT", info: "(Texto sem formatação)", disabled: true },
      ],
      checkAll: false,
      loaded: false,
      loading: false,
    }
  },
  async created() {
    await this.fetchCfcs()

    this.loaded = true
  },
  methods: {
    open() {
      this.$refs.modal.open()
    },
    close() {
      this.checkAll = false
      this.loading = false
      this.schemas = []
      this.mes = ''
      this.format = 'pdf'
      this.$refs.modal.close()
    },
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then((res) => {
          this.schemaOptions = res.body.dados
            .filter(cfc => cfc.nome_fantasia !== 'TESTE')
            .map(cfc => {
              const sindicatoSuffix = cfc.sindicato ? ' - Sindicato' : ''
              return {
                value: cfc.tenant_schema,
                text: `${cfc.nome_fantasia}${sindicatoSuffix}`,
              }
            })
        })
        .catch((error) => {
          this.$toasted.show('Erro!' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    async handleSubmit() {
      if (!this.validateInputs()) {
        return
      }

      this.loading = true

      const params = {
        periodo: this.mes,
        schemas: this.checkAll ? 'all' : this.schemas,
      }

      if (this.format === 'csv') {
        params.detalhado = true
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/faturamento/exportar/faturas/tipo/status`, {
        params,
      })
        .then((res) => {
          switch (this.format) {
            case 'pdf': {
              _export.downloadPDF(res.body.dados, 'faturamento-tipo-status')
              break
            }
            case 'csv': {
              _export.downloadCSV(res.body.dados, 'faturamento-tipo-status')
              break
            }
          }
        })
        .catch((err) => {
          this.$toasted.show(`Erro ao tentar gerar relatório: ${err.body.mensagem}`, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    validateInputs() {
      if (!this.checkAll && !this.schemas.length) {
        this.$toasted.show('Selecione uma autoescola.', {
          position: 'top-right',
        })

        return false;
      }

      if (!this.mes) {
        this.$toasted.show('O campo de data é obrigatório.', {
          position: 'top-right',
        })

        return false
      }

      return true
    },
  }
}
</script>

<style lang="scss" scoped>
.loading {
  width: 35vw;
  display: flex;
  justify-content: center;
  align-items: center;
}

.export-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .input-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    legend {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: #606264;
      line-height: 21.82px;
    }

    .inputs-box {
      max-height: 11rem;
      overflow-y: auto;
      outline: 0;
      padding: 0.625rem;
      border: 1px solid #EAEAEA;
      border-radius: 4px;

      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .checkbox {
        label {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          line-height: 19.1px;
          color: #606264;
          cursor: pointer;

          input[type="checkbox"] {
            display: none;
          }

          span {
            position: relative;
            display: inline-block;
            width: 1rem;
            height: 1rem;
            border: 2px solid #DFDCDC;
            border-radius: 2px;
            background-color: #FAFAFA;
            transition: background-color 0.2s, border-color 0.2s;
          }

          input[type="checkbox"]:checked+span {
            background-color: #1C2D5A;
          }

          input[type="checkbox"]:checked+span::after {
            content: '✓';
            color: white;
            font-size: 8px;
            position: absolute;
            top: 55%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }
      }
    }
  }

  .format-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    span {
      font-size: 1rem;
      font-weight: 600;
      line-height: 21.82px;
      color: #606264;
    }
  }

  button {
    justify-content: center;
  }
}
</style>